import { useReducer, useEffect } from 'react';
import { getYesterday, getTomorrow } from '../helpers/dateTimeHelper';

// Action types for reducer
export const ACTIONS = {
    SET_LOG_MESSAGE_TABLE: 'SET_LOG_MESSAGE_TABLE',
    SET_INPUT_VALUE: 'SET_INPUT_VALUE',
    SET_FILTER_WORDS: 'SET_FILTER_WORDS',
    SET_DATE_FROM: 'SET_DATE_FROM',
    SET_DATE_TO: 'SET_DATE_TO',
    SET_MAX_ROWS: 'SET_MAX_ROWS',
    SET_MAX_ROWS_INPUT_VALUE: 'SET_MAX_ROWS_INPUT_VALUE',
    SET_LATEST_UPDATE: 'SET_LATEST_UPDATE',
    SET_TRANSACTION_TAG_FILTER: 'SET_TRANSACTION_TAG_FILTER',
    SET_DROPDOWN_TABLE: 'SET_DROPDOWN_TABLE',
    SET_LOADING: 'SET_LOADING',
    SET_LOADING_DROPDOWN_OPTIONS: 'SET_LOADING_DROPDOWN_OPTIONS',
    INCREMENT_INPUT_ENTER_KEY: 'INCREMENT_INPUT_ENTER_KEY',
    INCREMENT_MAX_ROWS_INPUT_ENTER_KEY: 'INCREMENT_MAX_ROWS_INPUT_ENTER_KEY',
    SET_TOTAL_ROWS: 'SET_TOTAL_ROWS',
    SET_MODAL_OPEN: 'SET_MODAL_OPEN',
    SET_MODAL_DATA: 'SET_MODAL_DATA',
    RESET_MODAL: 'RESET_MODAL',
    RESET_FILTERS: 'RESET_FILTERS',
};

// Load dates from sessionStorage or use defaults
const getInitialDates = () => {
    const storageKey = 'shared_dateFilters';
    try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                dateFrom: parsed.dateFrom ? new Date(parsed.dateFrom) : getYesterday(),
                dateTo: parsed.dateTo ? new Date(parsed.dateTo) : getTomorrow(),
            };
        }
    } catch (error) {
        console.warn('Failed to load saved date filters:', error);
    }
    return {
        dateFrom: getYesterday(),
        dateTo: getTomorrow(),
    };
};

// Initial state factory
const createInitialState = (initialMaxRows = 100) => {
    const { dateFrom, dateTo } = getInitialDates();
    return {
        logMessageTable: [],
        inputValue: '',
        filterWords: '',
        dateFrom,
        dateTo,
        maxRows: initialMaxRows,
        maxRowsInputValue: initialMaxRows,
        latestUpdate: null,
        transactionTagFilter: null,
        dropdownTable: [],
        loading: false,
        loadingDropdownOptions: false,
        inputEnterKey: 0,
        maxRowsInputEnterKey: 0,
        totalRows: null,
        modalOpen: false,
        modalData: "",
    };
};

// Reducer function
function logTableReducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_LOG_MESSAGE_TABLE:
            return { ...state, logMessageTable: action.payload };
        case ACTIONS.SET_INPUT_VALUE:
            return { ...state, inputValue: action.payload };
        case ACTIONS.SET_FILTER_WORDS:
            return { ...state, filterWords: action.payload };
        case ACTIONS.SET_DATE_FROM:
            return { ...state, dateFrom: action.payload };
        case ACTIONS.SET_DATE_TO:
            return { ...state, dateTo: action.payload };
        case ACTIONS.SET_MAX_ROWS:
            return { ...state, maxRows: action.payload };
        case ACTIONS.SET_MAX_ROWS_INPUT_VALUE:
            return { ...state, maxRowsInputValue: action.payload };
        case ACTIONS.SET_LATEST_UPDATE:
            return { ...state, latestUpdate: action.payload };
        case ACTIONS.SET_TRANSACTION_TAG_FILTER:
            return { ...state, transactionTagFilter: action.payload };
        case ACTIONS.SET_DROPDOWN_TABLE:
            return { ...state, dropdownTable: action.payload };
        case ACTIONS.SET_LOADING:
            return { ...state, loading: action.payload };
        case ACTIONS.SET_LOADING_DROPDOWN_OPTIONS:
            return { ...state, loadingDropdownOptions: action.payload };
        case ACTIONS.INCREMENT_INPUT_ENTER_KEY:
            return { ...state, inputEnterKey: state.inputEnterKey + 1 };
        case ACTIONS.INCREMENT_MAX_ROWS_INPUT_ENTER_KEY:
            return { ...state, maxRowsInputEnterKey: state.maxRowsInputEnterKey + 1 };
        case ACTIONS.SET_TOTAL_ROWS:
            return { ...state, totalRows: action.payload };
        case ACTIONS.SET_MODAL_OPEN:
            return { ...state, modalOpen: action.payload };
        case ACTIONS.SET_MODAL_DATA:
            return { ...state, modalData: action.payload };
        case ACTIONS.RESET_MODAL:
            return { ...state, modalOpen: false, modalData: "" };
        case ACTIONS.RESET_FILTERS:
            return {
                ...state,
                inputValue: '',
                filterWords: '',
                dateFrom: getYesterday(),
                dateTo: getTomorrow(),
                maxRows: action.payload.initialMaxRows,
                maxRowsInputValue: action.payload.initialMaxRows,
                transactionTagFilter: null,
            };
        default:
            return state;
    }
}

// Custom hook for log table state management
export function useLogTableState(initialMaxRows = 100) {
    const [state, dispatch] = useReducer(logTableReducer, createInitialState(initialMaxRows));

    // Save dates to sessionStorage whenever they change
    useEffect(() => {
        const storageKey = 'shared_dateFilters';
        try {
            sessionStorage.setItem(storageKey, JSON.stringify({
                dateFrom: state.dateFrom.toISOString(),
                dateTo: state.dateTo.toISOString(),
            }));
        } catch (error) {
            console.warn('Failed to save date filters:', error);
        }
    }, [state.dateFrom, state.dateTo]);

    // Handler functions
    const handlers = {
        openModal: () => {
            dispatch({ type: ACTIONS.SET_MODAL_OPEN, payload: true });
        },

        closeModal: () => {
            dispatch({ type: ACTIONS.SET_MODAL_OPEN, payload: false });
        },

        resetModal: () => {
            dispatch({ type: ACTIONS.RESET_MODAL });
        },

        setModalData: (data) => {
            dispatch({ type: ACTIONS.SET_MODAL_DATA, payload: data });
        },

        handleInputChange: (e) => {
            dispatch({ type: ACTIONS.SET_INPUT_VALUE, payload: e.target.value });
            dispatch({ type: ACTIONS.SET_MAX_ROWS, payload: state.maxRowsInputValue });
        },

        handleInputKeyPress: (e) => {
            if (e.key === 'Enter') {
                dispatch({ type: ACTIONS.SET_FILTER_WORDS, payload: e.target.value });
                dispatch({ type: ACTIONS.INCREMENT_INPUT_ENTER_KEY });
            }
        },

        handleSearchClick: () => {
            dispatch({ type: ACTIONS.SET_FILTER_WORDS, payload: state.inputValue });
            dispatch({ type: ACTIONS.SET_MAX_ROWS, payload: state.maxRowsInputValue });
        },

        handleDateFromChange: (selectedDate) => {
            dispatch({ type: ACTIONS.SET_DATE_FROM, payload: selectedDate });

            if (!state.dateTo || selectedDate >= state.dateTo) {
                const nextDay = new Date(selectedDate);
                nextDay.setDate(nextDay.getDate() + 1);
                dispatch({ type: ACTIONS.SET_DATE_TO, payload: nextDay });
            }
        },

        handleDateToChange: (selectedDate) => {
            dispatch({ type: ACTIONS.SET_DATE_TO, payload: selectedDate });

            if (!state.dateFrom || selectedDate <= state.dateFrom) {
                const prevDay = new Date(selectedDate);
                prevDay.setDate(prevDay.getDate() - 1);
                dispatch({ type: ACTIONS.SET_DATE_FROM, payload: prevDay });
            }
        },

        handleMaxRowsChange: (e) => {
            dispatch({ type: ACTIONS.SET_MAX_ROWS_INPUT_VALUE, payload: e.target.value });
        },

        handleMaxRowsKeyPress: (e) => {
            if (e.key === 'Enter') {
                dispatch({ type: ACTIONS.SET_MAX_ROWS, payload: e.target.value });
                dispatch({ type: ACTIONS.INCREMENT_MAX_ROWS_INPUT_ENTER_KEY });
            }
        },

        handleTransactionTagClick: (tag) => {
            dispatch({ type: ACTIONS.SET_TRANSACTION_TAG_FILTER, payload: tag });
        },

        handleClearTransactionTagFilter: () => {
            dispatch({ type: ACTIONS.SET_TRANSACTION_TAG_FILTER, payload: null });
        },

        toggleDatePicker: (id) => {
            const datePicker = document.getElementById(id);
            if (datePicker) {
                datePicker.focus();
            }
        },

        resetFilters: () => {
            dispatch({ type: ACTIONS.RESET_FILTERS, payload: { initialMaxRows } });
        },
    };

    return {
        state,
        dispatch,
        ACTIONS,
        ...handlers,
    };
}
